import { useState } from 'react';
import { AlertTriangle, Ban, Sparkles, TrendingUp } from 'lucide-react';
import { Badge, Button, Modal, cn } from '../../ui';
import type { SprintInsights } from '../../features/sprintboard/insights/sprintInsights';

export interface SprintInsightsPanelProps {
  open: boolean;
  onClose: () => void;
  sprintName: string;
  insights: SprintInsights;
  /** Optional AI narrative generator (numbers are always local). */
  onNarrate: () => Promise<string>;
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'good' | 'bad';
}) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5">
      <p
        className={cn(
          'text-lg font-semibold tabular-nums',
          tone === 'good'
            ? 'text-emerald-300'
            : tone === 'bad'
              ? 'text-red-300'
              : 'text-zinc-100',
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-zinc-500">{label}</p>
    </div>
  );
}

/**
 * Sprint Insights (§21): a lightweight panel over the LOCALLY-computed numbers —
 * health, completed/remaining effort, remaining capacity, blockers, at-risk
 * count — plus a few plain-language recommendations. An optional ✨ narrative
 * asks Vector to interpret the same numbers (never to compute them).
 */
export function SprintInsightsPanel({
  open,
  onClose,
  sprintName,
  insights,
  onNarrate,
}: SprintInsightsPanelProps) {
  const [narrative, setNarrative] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runNarrate = async () => {
    setLoading(true);
    setError(null);
    try {
      setNarrative(await onNarrate());
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Vector could not summarize the sprint.',
      );
    } finally {
      setLoading(false);
    }
  };

  const atRisk = insights.health === 'at_risk';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" aria-hidden="true" />
          Sprint Insights ·{' '}
          <span className="font-normal text-zinc-400">{sprintName}</span>
        </span>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-400">Sprint health:</span>
          <Badge tone={atRisk ? 'warning' : 'success'} dot>
            {atRisk ? 'At risk' : 'On track'}
          </Badge>
          {insights.percentComplete > 0 && (
            <span className="text-sm text-zinc-500">
              · {insights.percentComplete}% complete
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Completed effort" value={`${insights.completedEffortDays}d`} tone="good" />
          <Metric label="Remaining effort" value={`${insights.remainingEffortDays}d`} />
          <Metric
            label="Remaining capacity"
            value={`${insights.remainingCapacityDays}d`}
            tone={insights.remainingCapacityDays < insights.remainingEffortDays ? 'bad' : 'default'}
          />
          <Metric
            label="Blocked"
            value={String(insights.blockedCount)}
            tone={insights.blockedCount > 0 ? 'bad' : 'default'}
          />
        </div>

        {insights.blocked.length > 0 && (
          <section>
            <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
              <Ban className="h-3.5 w-3.5" aria-hidden="true" />
              Blockers ({insights.blockedCount}) · at risk: {insights.atRiskCount}
            </h3>
            <ul className="space-y-1.5">
              {insights.blocked.map((b) => (
                <li
                  key={b.taskId}
                  className="flex items-start justify-between gap-3 rounded-lg border border-surface-border bg-surface-1 px-3 py-2"
                >
                  <span className="min-w-0 text-sm text-zinc-200">
                    <span className="truncate">{b.title}</span>
                    {b.reason && (
                      <span className="ml-1 text-xs text-zinc-500">— {b.reason}</span>
                    )}
                  </span>
                  <Badge tone={b.days >= 2 ? 'danger' : 'warning'}>
                    {b.days}d blocked
                  </Badge>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Recommendations
          </h3>
          <ul className="space-y-1.5">
            {insights.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                <AlertTriangle
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400"
                  aria-hidden="true"
                />
                {r}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-accent/30 bg-accent-soft/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
              <Sparkles className="h-4 w-4 text-accent-fg" aria-hidden="true" />
              Vector Insights
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Sparkles className="h-4 w-4" />}
              loading={loading}
              onClick={runNarrate}
            >
              {narrative ? 'Regenerate' : 'Summarize'}
            </Button>
          </div>
          {error && (
            <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {error}
            </p>
          )}
          {narrative && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">
              {narrative}
            </p>
          )}
          {!narrative && !error && (
            <p className="mt-1 text-xs text-zinc-500">
              A plain-language read of the numbers above — computed locally,
              interpreted by Vector.
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
}

export default SprintInsightsPanel;
